import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import IconMC from './IconMC';
import useClickOutside from '../../hooks/useClickOutside';

const COMPARISON_OPTIONS = [
  { id: 'greater_than', labelKey: 'comparison.greater_than' },
  { id: 'less_than', labelKey: 'comparison.less_than' },
  { id: 'equal', labelKey: 'comparison.equal' }
];

/**
 * AmountComparisonFilterMC - Filter component for amount with comparison operator
 * 
 * @param {string} currency - Currency symbol to display (e.g., 'USD')
 * @param {function} onChange - Callback when filter values change, receives { amount, comparison }
 * @param {string} defaultAmount - Default amount value
 * @param {string} defaultComparison - Default comparison operator
 */
const AmountComparisonFilterMC = ({
  currency = 'USD',
  onChange,
  defaultAmount = '',
  defaultComparison = 'greater_than'
}) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(defaultAmount);
  const [comparison, setComparison] = useState(defaultComparison);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const prevDefaultAmountRef = useRef(defaultAmount);
  const prevDefaultComparisonRef = useRef(defaultComparison);

  const domNode = useClickOutside(() => {
    setIsDropdownOpen(false);
  });

  // Only update from props if they changed externally (not from user input)
  useEffect(() => {
    if (prevDefaultAmountRef.current !== defaultAmount) {
      setAmount(defaultAmount);
      prevDefaultAmountRef.current = defaultAmount;
    }
    if (prevDefaultComparisonRef.current !== defaultComparison) {
      setComparison(defaultComparison);
      prevDefaultComparisonRef.current = defaultComparison;
    }
  }, [defaultAmount, defaultComparison]);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    if (onChange) {
      onChange({ amount: value, comparison });
    }
  };

  const handleComparisonSelect = (comparisonId) => {
    setComparison(comparisonId);
    setIsDropdownOpen(false);
    if (onChange) {
      onChange({ amount, comparison: comparisonId });
    }
  };

  const selectedOption = COMPARISON_OPTIONS.find((opt) => opt.id === comparison);
  const selectedComparisonLabel = selectedOption ? t(selectedOption.labelKey) : t('comparison.greater_than');

  return (
    <div className="tw-flex tw-gap-2">
      {/* Amount Input */}
      <div className="tw-flex-1 tw-relative">
        <input
          type="number"
          value={amount}
          onChange={handleAmountChange}
          placeholder="0"
          className="tw-w-full tw-px-4 tw-py-2.5 tw-pr-16 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl tw-text-default focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
        />
        <span className="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm">
          {currency}
        </span>
      </div>

      {/* Comparison Dropdown */}
      <div className="tw-relative tw-w-36" ref={domNode}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="tw-bg-gray-50 tw-text-default tw-rounded-xl tw-w-full tw-px-4 tw-py-2.5 tw-flex tw-items-center tw-justify-between tw-border tw-border-gray-200"
        >
          <span className="tw-block tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-sm">
            {selectedComparisonLabel}
          </span>
          <IconMC
            name={`arrow-ios-${isDropdownOpen ? 'up' : 'down'}ward-outline`}
            size="base"
            className="tw-text-gray-600"
          />
        </button>
        {isDropdownOpen && (
          <div className="tw-absolute tw-bg-white tw-border tw-rounded-md tw-shadow-lg tw-z-50 tw-w-full tw-mt-1">
            <ul className="tw-py-1 tw-overflow-y-auto tw-max-h-60 tw-pl-0">
              {COMPARISON_OPTIONS.map((option) => (
                <li
                  key={option.id}
                  onClick={() => handleComparisonSelect(option.id)}
                  className={`tw-px-4 tw-py-2 hover:tw-bg-blue-100 tw-flex tw-items-center tw-cursor-pointer tw-text-sm ${
                    comparison === option.id ? 'tw-bg-blue-500 tw-text-white' : ''
                  }`}
                >
                  {t(option.labelKey)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmountComparisonFilterMC;

